/**
 * @file events.ts
 *
 * Central registry of event name constants used across the App Suite.
 * Grouped by functional area.
 */

export const AS_APP_EVENTS = {
  /** Dispatched by <as-app> when connected. Detail: { sender: AsApp } */
  READY: 'as-app',
  /** Dispatched when aggregate busy state changes. Detail: { busy: boolean, keys: string[] } */
  BUSY: 'as-app-busy',
  /** Request to update ref-counted busy state. Detail: { key: string, busy: boolean } */
  BUSY_REQUEST: 'as-app-busy-request',
  /** Toggle the debug inspector. */
  INSPECTOR_TOGGLE: 'as-inspector-toggle',
  /** Trigger a UI notification. Detail: { defaultText, dictKey, type, persist, actions, resolve } */
  NOTIFY: 'as-notify',
  /** Close a persistent notification. Detail: { id: string } */
  CLOSE_NOTIFICATION: 'as-close-notification',
  /** Standard button click event from header/footer. Detail: { buttonName: string } */
  BUTTON_CLICK: 'as-button-click',
  /** Request to open the global modal overlay. Detail: { visibleChild?: string } */
  MODAL_OPEN: 'as-open-modal',
  /** Request to close the global modal overlay. */
  MODAL_CLOSE: 'as-close-modal',
  /** Set the active child ID in the modal overlay. Detail: { id: string } */
  MODAL_SET_VISIBLE_CHILD: 'as-set-visible-child',
  /** Dispatched by the overlay when it finishes closing. */
  MODAL_CLOSED: 'as-modal-closed',
  /** Trigger a temporary visual flash/highlight on an element. Detail: { target: string | HTMLElement } */
  FLASH_ELEMENT: 'as-flash-element',
} as const;

export const AS_PROJECT_EVENTS = {
  /** Trigger creation of a fresh project. */
  NEW: 'as-project-new',
  /** Dispatched after a project is successfully loaded. Detail: { project: Project } */
  LOADED: 'as-project-loaded',
  /** Dispatched after a project is successfully saved. */
  SAVED: 'as-project-saved',
  /** Request to load a project by ID or from file list. */
  LOAD_REQUEST: 'as-project-load-request',
  /** Dispatched when the saved projects list is updated. Detail: { projects: Project[] } */
  LIST_UPDATED: 'as-projects-list',
  /** Dispatched after UI inputs and CAD state are restored. Detail: { customData } */
  SESSION_RESTORED: 'session-restored',
  /** Triggered when a .asproject file is dropped/selected. Detail: { blob } */
  RESTORE_FILE: 'restore-session-file',
  /** Window event: reset all inputs to defaults. Fired by managed project handling
   *  (createAndSetNewProject). Detail: { silenceChange?: boolean } */
  INPUTS_RESET: 'as-inputs-reset',
  /** Window event: explicit hook for hosts running with disable-session-handlers to
   *  reset session-scoped component state (e.g. <as-export-step>) on their own
   *  "new session", since the managed as-inputs-reset broadcast never fires there. */
  MANUAL_RESET: 'as-session-manual-reset',
  /** Dispatched when any input change occurs. */
  INPUT_CHANGE: 'as-input-change',
  /** Dispatched when customData changes. */
  CUSTOM_DATA_CHANGED: 'as-custom-data-changed',
} as const;

export const AS_CONFIGURATOR_EVENTS = {
  /** Dispatched when AsConfigurator component is ready. Detail: { sender: AsConfigurator } */
  COMP_READY: 'as-cfgr-comp-ready',
  /** Dispatched by Paramate when a session is initialized. */
  PARAMATE_INIT: 'paramate-configurator-initialized',
  /** Dispatched by Paramate before a configuration cycle. */
  PARAMATE_BEFORE_CONFIGURE: 'paramate-configurator-before-configure',
  /** Dispatched by Paramate after a configuration cycle. */
  PARAMATE_AFTER_CONFIGURE: 'paramate-configurator-after-configure',
  /** Dispatched by Paramate after a session restore. */
  PARAMATE_AFTER_RESTORE: 'paramate-configurator-after-restore',
  /** Dispatched when Paramate encounters an error. */
  PARAMATE_ERROR: 'paramate-configurator-error',
  /** Trigger a CAD configuration cycle. Detail: { params?, skipHistory? } */
  CONFIGURE: 'as-configure',
  /** Trigger configure if any values are pending. */
  CONFIGURE_PENDING: 'as-configure-pending',
  /** Request to upload a file. Detail: { file, fileParamName, resolve, reject, ... } */
  UPLOAD_FILE: 'upload-file',
  /** Request to delete a file. Detail: { fileParamName } */
  DELETE_FILE: 'delete-file',
  /** Reset the configurator to its initial state. */
  RESET: 'as-reset-configurator',
  /** Session keepalive heartbeat. */
  HEARTBEAT: 'as-heartbeat',
  /** The keepalive found the server session dead (401/404) — e.g. the machine slept
   *  past the session TTL. Detail: { error }. Routed into the expired-session flow. */
  SESSION_LOST: 'as-session-lost',
} as const;

export const AS_MESH_EVENTS = {
  /** Request a mesh download. Detail: { finalized, type, fileHandle } */
  DOWNLOAD_REQUEST: 'as-download-mesh',
  /** Request a mesh blob from the configurator. Detail: { finalized, type, filename, resolve, reject } */
  GET_MESH: 'as-get-configurator-mesh',
  /** Request a temporary URL for a mesh. Detail: { type, filename, finalized, resolve, reject } */
  GET_MESH_URL: 'get-mesh-url',
  /** Dispatched when a transferred mesh is applied from store. Detail: { mesh, meshId, stl }.
   *  Suppressed when the app set as-app.onTransferMeshLoaded — the callback replaces the event. */
  DB_MESH_LOADED: 'as-db-mesh-loaded',
  /** Confirm a received transfer mesh was processed. Detail: { meshId, shouldDelete?, error? }.
   *  error marks a failure resolution: cleanup runs but the success data_import
   *  log is skipped (the failure was already logged as data_import_error). */
  MESH_RX_COMPLETED: 'as-mesh-rx-completed',
} as const;

/**
 * Unified data-I/O completion events. Caught by <as-app> and turned into server
 * logs (data-import / data-export). The detail carries a `subcategory`
 * ('local' | 'interapp' | 'grabcad') and a `fileInfo` array; the mesh-vs-file
 * distinction is conveyed by fileInfo[].fileType rather than separate events.
 * Failures ride the same events via detail.error, but land server-side under
 * the distinct data_import_error / data_export_error log types.
 */
export const AS_FILE_EVENTS = {
  /** A data import completed (or failed). Detail: { subcategory, fileInfo, info?, error? } */
  IMPORT: 'as-data-import',
  /** A data export completed (or failed). Detail: { subcategory, fileInfo?, assemblyId?, toApp?, error? } */
  EXPORT: 'as-data-export',
} as const;

export const AS_HISTORY_EVENTS = {
  /** Trigger an undo operation. */
  UNDO: 'as-undo',
  /** Trigger a redo operation. */
  REDO: 'as-redo',
  /** Clear the entire history stack. */
  CLEAR: 'as-history-clear',
  /** Dispatched when an undo starts. Detail: { action } */
  UNDO_START: 'as-undo-start',
  /** Dispatched when a redo starts. Detail: { action } */
  REDO_START: 'as-redo-start',
  /** Request for batch start. Detail: { label, tags } */
  BATCH_START: 'as-history-batch-start',
  /** Request for batch end. */
  BATCH_END: 'as-history-batch-end',
  /** Request for batch cancel. */
  BATCH_CANCEL: 'as-history-batch-cancel',
} as const;

/** Aggregate of all event names for type-safety and easy lookups. */
export const AS_EVENTS = {
  APP: AS_APP_EVENTS,
  PROJECT: AS_PROJECT_EVENTS,
  CONFIGURATOR: AS_CONFIGURATOR_EVENTS,
  MESH: AS_MESH_EVENTS,
  FILE: AS_FILE_EVENTS,
  HISTORY: AS_HISTORY_EVENTS,
} as const;

/** Union of all event name string values. */
export type AsEventName =
  | typeof AS_APP_EVENTS[keyof typeof AS_APP_EVENTS]
  | typeof AS_PROJECT_EVENTS[keyof typeof AS_PROJECT_EVENTS]
  | typeof AS_CONFIGURATOR_EVENTS[keyof typeof AS_CONFIGURATOR_EVENTS]
  | typeof AS_MESH_EVENTS[keyof typeof AS_MESH_EVENTS]
  | typeof AS_FILE_EVENTS[keyof typeof AS_FILE_EVENTS]
  | typeof AS_HISTORY_EVENTS[keyof typeof AS_HISTORY_EVENTS];
